import { useState } from 'react'
import { Camera, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { usePlotReadingStatuses } from '@/hooks/use-meter-readings'
import { MeterReadingModal } from '@/components/utilities/meter-reading-modal'
import { BulkReadingUploadModal } from './bulk-reading-upload-modal'
import type { MeterReadingStatus, PlotReadingStatus, UtilityType } from '@riverpark/types'

interface PlotReadingsChecklistProps {
  utilityType: UtilityType
  billingPeriod: string
}

function StatusBadge({ status }: { status: MeterReadingStatus }) {
  switch (status) {
    case 'posted':
      return <Badge variant="paid">Posted</Badge>
    case 'draft':
      return <Badge variant="draft">Draft</Badge>
    case 'pending_correction':
      return <Badge variant="pending">Correction pending</Badge>
    case 'photo_pending':
      return <Badge variant="invited">Photo pending</Badge>
    default:
      return <Badge variant="default">Not entered</Badge>
  }
}

function ReadingActionButton({ plot, onClick }: { plot: PlotReadingStatus; onClick: () => void }) {
  const locked = plot.status === 'posted' || plot.status === 'pending_correction'
  const label =
    plot.status === 'not_entered'
      ? 'Enter reading'
      : plot.status === 'photo_pending'
        ? 'Add photo'
        : 'Edit'

  return (
    <Button variant="secondary" size="sm" disabled={locked} onClick={onClick}>
      <Camera size={14} className="mr-1.5" />
      {label}
    </Button>
  )
}

export function PlotReadingsChecklist({ utilityType, billingPeriod }: PlotReadingsChecklistProps) {
  const { data, isLoading } = usePlotReadingStatuses(utilityType.id, billingPeriod)
  const [activePlot, setActivePlot] = useState<PlotReadingStatus | null>(null)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const plotStatuses = data?.data ?? []
  const enteredCount = plotStatuses.filter((p) => p.status !== 'not_entered').length

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading plots…</p>
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-sans text-sm text-muted-foreground">
          {enteredCount} of {plotStatuses.length} plots entered
        </p>
        <Button variant="secondary" size="sm" onClick={() => setBulkUploadOpen(true)}>
          <Upload size={14} className="mr-1.5" />
          Bulk upload
        </Button>
      </div>

      {plotStatuses.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No occupied plots found.
        </p>
      ) : (
        <>
          {/* Mobile: stacked cards with the action up front, not buried past scrollable columns. */}
          <div className="space-y-2 sm:hidden">
            {plotStatuses.map((plot) => (
              <div key={plot.plotId} className="rounded border border-border-2 bg-card p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-sm font-medium text-ink">
                      Plot {plot.plotNumber}
                    </span>
                    <StatusBadge status={plot.status} />
                  </div>
                  <ReadingActionButton plot={plot} onClick={() => setActivePlot(plot)} />
                </div>
                <div className="mt-2 flex gap-4 font-sans text-xs text-muted-foreground">
                  <span>
                    Previous{' '}
                    <span className="font-medium text-ink">
                      {plot.reading?.previousReading ?? plot.suggestedPreviousReading}
                    </span>
                  </span>
                  <span>
                    Current{' '}
                    <span className="font-medium text-ink">
                      {plot.reading?.currentReading ?? '—'}
                    </span>
                  </span>
                  <span>
                    Units{' '}
                    <span className="font-medium text-ink">
                      {plot.reading?.unitsConsumed ?? '—'}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop / tablet: table, horizontally scrollable as a safety net. */}
          <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Plot</th>
                  <th className="px-4 py-3">Previous</th>
                  <th className="px-4 py-3">Current</th>
                  <th className="px-4 py-3">Units</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {plotStatuses.map((plot) => (
                  <tr key={plot.plotId} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink">{plot.plotNumber}</td>
                    <td className="px-4 py-3 text-ink">
                      {plot.reading?.previousReading ?? plot.suggestedPreviousReading}
                    </td>
                    <td className="px-4 py-3 text-ink">{plot.reading?.currentReading ?? '—'}</td>
                    <td className="px-4 py-3 text-ink">{plot.reading?.unitsConsumed ?? '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={plot.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ReadingActionButton plot={plot} onClick={() => setActivePlot(plot)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activePlot && (
        <MeterReadingModal
          open
          onClose={() => setActivePlot(null)}
          utilityType={utilityType}
          billingPeriod={billingPeriod}
          plotStatus={activePlot}
        />
      )}

      <BulkReadingUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        utilityType={utilityType}
        billingPeriod={billingPeriod}
        plots={plotStatuses}
      />
    </div>
  )
}

/** Read-only progress check — lets a parent (e.g. the Generate Invoices gate) know completion state. */
export function useReadingsProgress(utilityTypeId: string, billingPeriod: string) {
  const { data, isLoading } = usePlotReadingStatuses(utilityTypeId, billingPeriod)
  const plotStatuses = data?.data ?? []
  const total = plotStatuses.length
  const entered = plotStatuses.filter((p) => p.status !== 'not_entered').length
  const photosPending = plotStatuses.filter((p) => p.status === 'photo_pending').length
  // Billable now — a photo is no longer required to generate, so 'draft' and 'photo_pending'
  // both count. Generation can run as soon as any plot is billable; the rest are just skipped.
  const billable = plotStatuses.filter(
    (p) => p.status === 'draft' || p.status === 'photo_pending',
  ).length
  return {
    isLoading,
    total,
    entered,
    photosPending,
    billable,
    complete: total > 0 && entered === total,
    ready: total > 0 && billable > 0,
  }
}
